import {
  RECURRENCE_DAYS,
  RecurrenceEndType,
  RecurrenceType,
  RecurrenceTypePayload,
  WEEKDAYS,
} from '@/core/constants/schedule/recurrence';
import DateFormats from '@/core/constants/DateFormats';
import moment from 'moment';

export const initialRecurrence = () => ({
  type: RecurrenceType.NONE,
  interval: 1,
  byDay: [],
  endType: RecurrenceEndType.NEVER,
  until: null,
  count: null,
});

export default {
  data: () => {
    return {
      RECURRENCE_DAYS,
      isLoadingRecurrence: false,
      recurrence: initialRecurrence(),
      isRecurrenceEndDatePickerOpen: false,
    };
  },
  computed: {
    canHaveRecurrence() {
      if (!this.form?.startDateTime) return true;
      return moment(this.form.startDateTime).isSameOrAfter(moment(), 'day');
    },
    recurrenceDisabledReason() {
      if (!this.form?.startDateTime)
        return 'Primero, selecciona una fecha de inicio';
      if (!this.canHaveRecurrence)
        return 'No se permiten recurrencias en eventos pasados';
      return null;
    },
    isCustomRecurrence() {
      return this.recurrence.type === RecurrenceType.CUSTOM;
    },
    isRecurrenceEnabled() {
      return this.recurrence.type !== RecurrenceType.NONE;
    },
    showByDay() {
      return this.isCustomRecurrence;
    },
    showInterval() {
      return this.isRecurrenceEnabled;
    },
    intervalSuffix() {
      const suffixes = {
        [RecurrenceType.DAILY]: 'día(s)',
        [RecurrenceType.WEEKDAYS]: 'semana(s)',
        [RecurrenceType.WEEKLY]: 'semana(s)',
        [RecurrenceType.MONTHLY]: 'mes(es)',
        [RecurrenceType.YEARLY]: 'año(s)',
        [RecurrenceType.CUSTOM]: 'semana(s)',
      };

      return suffixes[this.recurrence.type] || '';
    },
    recurrencePayload() {
      if (!this.isRecurrenceEnabled) {
        return {
          frequency: null,
          interval: null,
          byDay: [],
          until: null,
          repeatTimes: null,
        };
      }

      const base = this.isCustomRecurrence
        ? {
            frequency: 'weekly',
            interval: this.recurrence.interval,
            byDay: this.recurrence.byDay,
          }
        : {
            ...RecurrenceTypePayload[this.recurrence.type],
            interval: this.recurrence.interval,
          };

      return {
        ...base,
        until:
          this.recurrence.endType === RecurrenceEndType.UNTIL
            ? moment(this.recurrence.until)
                .endOf('day')
                .format(DateFormats.ISO_DATETIME)
            : null,
        repeatTimes:
          this.recurrence.endType === RecurrenceEndType.COUNT
            ? this.recurrence.count
            : null,
      };
    },
    isCustomRecurrenceValid() {
      if (!this.isCustomRecurrence) return true;
      return this.recurrence.byDay.length > 0;
    },
    isUntilValid() {
      if (this.recurrence.endType !== RecurrenceEndType.UNTIL) return true;
      return !!this.recurrence.until;
    },
    isCountValid() {
      if (this.recurrence.endType !== RecurrenceEndType.COUNT) return true;
      return this.recurrence.count >= 2;
    },
    isIntervalValid() {
      return this.recurrence.interval >= 1;
    },
    isRecurrenceValid() {
      if (!this.isRecurrenceEnabled) return true;
      return (
        this.isCustomRecurrenceValid &&
        this.isUntilValid &&
        this.isCountValid &&
        this.isIntervalValid
      );
    },
  },
  watch: {
    'recurrence.type'() {
      if (this.isLoadingRecurrence) return;

      this.recurrence = {
        ...this.recurrence,
        interval: 1,
        byDay: [],
        endType: RecurrenceEndType.NEVER,
        until: null,
        count: null,
      };
    },
    'form.startDateTime'(val) {
      if (val && moment(val).isBefore(moment(), 'day')) {
        this.resetRecurrence();
      }
    },
  },
  methods: {
    resetRecurrence() {
      this.recurrence = initialRecurrence();
    },
    toggleRecurrenceDay(day) {
      const index = this.recurrence.byDay.indexOf(day);
      index === -1
        ? this.recurrence.byDay.push(day)
        : this.recurrence.byDay.splice(index, 1);
    },
    loadRecurrence(event) {
      this.isLoadingRecurrence = true;

      const recurrence = event?.recurrence;
      const frequency = recurrence?.frequency?.value;

      if (!frequency) {
        this.resetRecurrence();
        this.$nextTick(() => {
          this.isLoadingRecurrence = false;
        });
        return;
      }

      const byDay = recurrence.byDay?.map((d) => d.value) ?? [];

      this.recurrence = {
        type: this.getRecurrenceType({ frequency, byDay }),
        interval: recurrence.interval || 1,
        byDay,
        endType: recurrence.until
          ? RecurrenceEndType.UNTIL
          : recurrence.repeatTimes
          ? RecurrenceEndType.COUNT
          : RecurrenceEndType.NEVER,
        until: recurrence.until
          ? moment(recurrence.until).format('YYYY-MM-DD')
          : null,
        count: recurrence.repeatTimes || null,
      };

      this.$nextTick(() => {
        this.isLoadingRecurrence = false;
      });
    },
    getRecurrenceType({ frequency, byDay }) {
      if (!frequency) return RecurrenceType.NONE;
      if (frequency === 'daily') return RecurrenceType.DAILY;
      if (frequency === 'monthly') return RecurrenceType.MONTHLY;
      if (frequency === 'yearly') return RecurrenceType.YEARLY;

      if (frequency === 'weekly') {
        const isWeekdays =
          byDay?.length === 5 && WEEKDAYS.every((d) => byDay.includes(d));

        if (isWeekdays) return RecurrenceType.WEEKDAYS;
        if (!byDay?.length) return RecurrenceType.WEEKLY;
        return RecurrenceType.CUSTOM;
      }

      return RecurrenceType.NONE;
    },
  },
};
